import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { directivesServiceUrl } from '../../../../_services/common.service';
import { ENTITY_TYPE_KEY, compileFailureMessage, directiveIdentity } from '../../../../utils/directives';

/**
 * Arrangement directives prompt panel (Directives PRD §6 — the "canvas").
 *
 * Free-text instruction → POST /directives/compile (the intent compiler) →
 * the parent applies the validated directive and re-runs ARRANGE.
 *
 * Entity anchors: typing @ or # opens an autocomplete over the edition's
 * entities (brands / suppliers / subcategories / categories). A picked
 * entity is inserted into the text by name AND recorded as a pre-resolved
 * reference ({type, id, name}) sent to the compiler — anchored entities
 * bypass name matching entirely, so the instruction cannot be ambiguous
 * about them.
 *
 * The catalog behind the autocomplete prefers the simulation-derived one
 * (buildCatalog) and falls back to the edition catalog fetched from the
 * directives service, so @ works before Arrange has ever run. When both
 * are empty the box is disabled with an explanation.
 *
 * All user-facing text is i18n (react-i18next); the panel direction follows
 * the active language, so it reads correctly in both Hebrew and English.
 *
 * onApply may reject the compiled directive as a duplicate (same identity —
 * (type, normalised scope) — as one already applied); the panel then names
 * the duplicated directive and keeps the text for editing.
 */
const COMPILE_URL =
  process.env.REACT_APP_DIRECTIVES_COMPILE_URL ||
  'http://localhost:8200/directives/compile';

const CATALOG_LISTS = ['brands', 'suppliers', 'subcategories', 'categories'];
const isEmptyCatalog = (cat) =>
  !cat || CATALOG_LISTS.every((k) => !(cat[k] || []).length);

const styles = {
  panel: {
    background: '#f7f5ff',
    border: '1px solid #d9d2f0',
    borderRadius: 8,
    padding: '10px 14px',
    margin: '8px 0',
    position: 'relative',
  },
  title: { fontWeight: 700, fontSize: 13, marginBottom: 6, color: '#4b3f8f' },
  row: { display: 'flex', gap: 8, alignItems: 'center' },
  input: {
    flex: 1,
    padding: '6px 10px',
    borderRadius: 6,
    border: '1px solid #c9c2e8',
    fontSize: 13,
  },
  send: {
    padding: '6px 16px',
    borderRadius: 6,
    border: 'none',
    background: '#6c5ce7',
    color: '#fff',
    cursor: 'pointer',
    fontSize: 13,
  },
  hint: { marginTop: 4, fontSize: 11, color: '#8a82b8' },
  notice: {
    fontSize: 12,
    color: '#8a6d1a',
    background: '#fdf6e3',
    border: '1px solid #e6d9a8',
    borderRadius: 6,
    padding: '8px 12px',
    marginBottom: 8,
  },
  echo: { marginTop: 6, fontSize: 12, color: '#333' },
  error: { marginTop: 6, fontSize: 12, color: '#b00020' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#fff',
    border: '1px solid #c9c2e8',
    borderRadius: 16,
    padding: '3px 10px',
    fontSize: 12,
  },
  anchorChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    background: '#ece8fb',
    border: '1px solid #b7abe8',
    borderRadius: 12,
    padding: '2px 8px',
    fontSize: 11,
    color: '#4b3f8f',
  },
  chipBtn: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 12,
    padding: 0,
  },
  popover: {
    position: 'absolute',
    zIndex: 1200,
    top: 64,
    right: 14,
    left: 90,
    maxHeight: 220,
    overflowY: 'auto',
    background: '#fff',
    border: '1px solid #b7abe8',
    borderRadius: 8,
    boxShadow: '0 4px 14px rgba(76,63,143,0.18)',
    padding: 4,
  },
  groupLabel: { fontSize: 11, color: '#8a82b8', padding: '4px 8px 2px' },
  option: (active) => ({
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 13,
    cursor: 'pointer',
    background: active ? '#ece8fb' : 'transparent',
  }),
};

const TRIGGER_RE = /[@#]([^@#]*)$/;

const ArrangementPromptPanel = ({
  directives,
  onApply,
  onUndo,
  onPromote,
  buildCatalog,
  editionId,
  disabled,
}) => {
  const { t, i18n } = useTranslation();
  const dir = i18n && i18n.language === 'he' ? 'rtl' : 'ltr';
  const [text, setText] = useState('');
  const [anchors, setAnchors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [echo, setEcho] = useState(null);
  const [problem, setProblem] = useState(null);
  // Identities (type + normalised scope) of the directives already saved to
  // this edition. A session directive is marked "saved" when its identity is
  // in here — never by its position in the list, which shifts on undo.
  const [savedIdentities, setSavedIdentities] = useState(() => new Set());
  const [popover, setPopover] = useState(null); // {query, options, active}
  const [editionCatalog, setEditionCatalog] = useState(null); // null until fetched
  const inputRef = useRef(null);

  // The simulation-derived catalog is empty until Arrange has run. Fetch the
  // edition catalog (same endpoint the Rules panel uses) so @ works right
  // after simulation. On fetch failure editionCatalog stays null — the panel
  // degrades to the simulation-derived catalog and never claims "no entities";
  // when that leaves nothing to anchor with, say the load failed rather
  // than staying silent.
  useEffect(() => {
    let cancelled = false;
    setEditionCatalog(null);
    if (!editionId || editionId === -1) return undefined;
    fetch(`${directivesServiceUrl}/directives/catalog?editionId=${editionId}`)
      .then((res) => {
        if (!res.ok) throw new Error(`catalog fetch failed: ${res.status}`);
        return res.json();
      })
      .then((cat) => {
        if (!cancelled) setEditionCatalog(cat || {});
      })
      .catch(() => {
        if (!cancelled && isEmptyCatalog(buildCatalog ? buildCatalog() : {})) {
          setProblem(t('DIR_LOAD_FAILED'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [editionId]);

  // The edition's already-saved directives, keyed by identity. Fetched when the
  // panel opens so a session directive that was promoted in an earlier visit
  // shows its "saved" mark again. On failure the set stays empty — the panel
  // just offers to promote; the server add is idempotent, so re-promoting an
  // already-saved directive is harmless.
  useEffect(() => {
    let cancelled = false;
    setSavedIdentities(new Set());
    if (!editionId || editionId === -1) return undefined;
    fetch(`${directivesServiceUrl}/directives?editionId=${editionId}`)
      .then((res) => {
        if (!res.ok) throw new Error(`directives fetch failed: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const identities = (data && data.directives ? data.directives : []).map(
          directiveIdentity,
        );
        setSavedIdentities(new Set(identities));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [editionId]);

  // The simulation-derived catalog wins when non-empty (it reflects the
  // entities actually on the fixture); otherwise fall back to the edition
  // catalog. Sent to the compiler too, so anchored names resolve before Arrange.
  const effectiveCatalog = () => {
    const sim = buildCatalog ? buildCatalog() : {};
    return isEmptyCatalog(sim) ? editionCatalog || {} : sim;
  };

  // Only claim "no entities" once the edition catalog has actually loaded.
  const catalogEmpty = editionCatalog !== null && isEmptyCatalog(effectiveCatalog());
  const inputDisabled = disabled || catalogEmpty;

  const flattenCatalog = () => {
    const cat = effectiveCatalog();
    const flat = [];
    const push = (list, type) =>
      (list || []).forEach((e) => flat.push({ type, id: e.id, name: e.name }));
    push(cat.brands, 'brand');
    push(cat.suppliers, 'supplier');
    push(cat.subcategories, 'subcategory');
    push(cat.categories, 'category');
    return flat;
  };

  const refreshOptions = (value, caret) => {
    const upToCaret = value.slice(0, caret);
    const m = TRIGGER_RE.exec(upToCaret);
    if (!m) return setPopover(null);
    const query = (m[1] || '').trim().toLowerCase();
    const options = flattenCatalog()
      .filter((e) => !query || String(e.name).toLowerCase().includes(query))
      .slice(0, 30);
    setPopover(options.length ? { query: m[1] || '', options, active: 0 } : null);
  };

  const pickOption = (opt) => {
    const el = inputRef.current;
    const caret = el ? el.selectionStart : text.length;
    const upToCaret = text.slice(0, caret);
    const m = TRIGGER_RE.exec(upToCaret);
    if (!m) return;
    const start = upToCaret.length - m[0].length;
    const nextText = text.slice(0, start) + opt.name + ' ' + text.slice(caret);
    setText(nextText);
    setAnchors((prev) =>
      prev.some((a) => a.type === opt.type && a.id === opt.id) ? prev : [...prev, opt],
    );
    setPopover(null);
    if (el) setTimeout(() => el.focus(), 0);
  };

  const onKeyDown = (e) => {
    if (popover) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        setPopover((p) => ({
          ...p,
          active: (p.active + delta + p.options.length) % p.options.length,
        }));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pickOption(popover.options[popover.active]);
        return;
      }
      if (e.key === 'Escape') {
        // Dismiss only the popover — don't let Esc bubble to the simulator's
        // "close simulator?" confirm.
        e.preventDefault();
        e.stopPropagation();
        setPopover(null);
        return;
      }
    }
    if (e.key === 'Enter') send();
  };

  const send = async () => {
    const instruction = text.trim();
    if (!instruction || busy || inputDisabled) return;
    setBusy(true);
    setProblem(null);
    setEcho(null);
    try {
      const usedAnchors = anchors.filter((a) => text.includes(a.name));
      const res = await fetch(COMPILE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instruction,
          catalog: effectiveCatalog(),
          activeDirectives: directives.map((d) => d.directive),
          references: usedAnchors,
          // Fallback language only — the compiler answers in the language of the
          // instruction itself; this decides the language when it cannot tell.
          locale: (i18n.language || '').split('-')[0],
        }),
      });
      const data = await res.json();
      if (data.status === 'ok') {
        const result = onApply({
          directive: data.directive,
          interpretation: data.interpretation || instruction,
        });
        if (result && result.accepted === false) {
          // Duplicate of an already-applied directive — keep the text so the
          // user can rephrase, and name the directive it duplicates.
          setProblem(t('DIR_DUPLICATE', { name: result.duplicateOf }));
        } else {
          setEcho(data.interpretation || t('DIR_RECEIVED'));
          setText('');
          setAnchors([]);
        }
      } else if (data.status === 'clarification') {
        setProblem(
          (compileFailureMessage(data, t) || t('DIR_CLARIFY')) +
            (data.options && data.options.length
              ? ' | ' + t('DIR_OPTIONS') + ' ' + data.options.join(', ')
              : ''),
        );
      } else {
        setProblem(compileFailureMessage(data, t) || t('DIR_NOT_UNDERSTOOD'));
      }
    } catch (e) {
      setProblem(t('DIR_COMM_ERROR'));
    } finally {
      setBusy(false);
    }
  };

  const promote = async (idx) => {
    try {
      const ok = await onPromote(idx);
      if (ok) {
        const identity = directiveIdentity(directives[idx]?.directive);
        setSavedIdentities((prev) => new Set(prev).add(identity));
      }
    } catch (e) {
      setProblem(t('DIR_PROMOTE_FAILED'));
    }
  };

  const grouped = popover
    ? ['brand', 'supplier', 'subcategory', 'category']
        .map((type) => ({ type, items: popover.options.filter((o) => o.type === type) }))
        .filter((g) => g.items.length)
    : [];

  return (
    <div style={{ ...styles.panel, direction: dir }}>
      <div style={styles.title}>{t('DIR_TITLE')}</div>
      {catalogEmpty ? <div style={styles.notice}>{t('DIR_CATALOG_NOTICE')}</div> : null}
      <div style={styles.row}>
        <input
          ref={inputRef}
          style={styles.input}
          value={text}
          disabled={busy || inputDisabled}
          placeholder={catalogEmpty ? t('DIR_PLACEHOLDER_NEED_CALC') : t('DIR_PLACEHOLDER_SIM')}
          onChange={(e) => {
            setText(e.target.value);
            refreshOptions(e.target.value, e.target.selectionStart);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => setTimeout(() => setPopover(null), 180)}
        />
        <button style={styles.send} onClick={send} disabled={busy || inputDisabled}>
          {busy ? t('DIR_TRANSLATING') : t('DIR_APPLY')}
        </button>
      </div>
      {anchors.length ? (
        <div style={styles.chips}>
          {anchors.map((a, i) => (
            <span style={styles.anchorChip} key={`${a.type}-${a.id}`}>
              <span>{t(ENTITY_TYPE_KEY[a.type])}: {a.name}</span>
              <button
                style={{ ...styles.chipBtn, color: '#4b3f8f' }}
                onClick={() => setAnchors(anchors.filter((_, j) => j !== i))}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div style={styles.hint}>{t('DIR_HINT_ANCHOR')}</div>
      )}
      {popover ? (
        <div style={styles.popover}>
          {grouped.map((g) => (
            <div key={g.type}>
              <div style={styles.groupLabel}>{t(ENTITY_TYPE_KEY[g.type])}</div>
              {g.items.map((opt) => {
                const flatIdx = popover.options.indexOf(opt);
                return (
                  <div
                    key={`${opt.type}-${opt.id}`}
                    style={styles.option(flatIdx === popover.active)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickOption(opt);
                    }}
                    onMouseEnter={() => setPopover((p) => ({ ...p, active: flatIdx }))}
                  >
                    {opt.name}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
      {echo ? <div style={styles.echo}>{t('DIR_ECHO_PREFIX')} {echo}</div> : null}
      {problem ? <div style={styles.error}>{problem}</div> : null}
      {directives.length ? (
        <div style={styles.chips}>
          {directives.map((d, idx) => {
            const identity = directiveIdentity(d.directive);
            const saved = savedIdentities.has(identity);
            return (
            <span style={styles.chip} key={identity}>
              <span>{d.interpretation || d.directive.type}</span>
              {saved ? (
                <span style={{ color: '#2e7d32' }}>✓ {t('DIR_SAVED_TO_EDITION')}</span>
              ) : (
                <button style={{ ...styles.chipBtn, color: '#4b3f8f' }} onClick={() => promote(idx)}>
                  {t('DIR_PROMOTE')}
                </button>
              )}
              <button style={{ ...styles.chipBtn, color: '#b00020' }} onClick={() => onUndo(idx)}>
                ✕ {t('DIR_UNDO')}
              </button>
            </span>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default ArrangementPromptPanel;